<template>
  <div class="legal-search" v-click-outside="closeSuggestions">
    <div class="legal-search__bar">
      <div class="legal-search__input-wrapper">
        <input
          v-model="inputValue"
          class="legal-search__input"
          type="text"
          placeholder="搜索规则关键词"
          @input="handleInput"
          @keydown.enter="handleSearch"
        />
        <span
          v-if="inputValue"
          class="legal-search__clear"
          @click="handleClear"
        >&times;</span>
      </div>
      <button class="legal-search__btn" @click="handleSearch">搜索</button>
    </div>
    <div v-if="showSuggestions && suggestions.length > 0" class="legal-search__suggestions">
      <div
        v-for="(item, index) in suggestions"
        :key="index"
        class="legal-search__suggestion-item"
        @click="handleSuggestionClick(item)"
      >
        <span class="legal-search__suggestion-text" v-html="item.title"></span>
        <span class="legal-search__suggestion-arrow">&#x203A;</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import zzfetch from '@zz/fetch'

interface SuggestionItem {
  title: string
  jumpUrl: string
}

export default Vue.extend({
  name: 'LegalSearch',
  directives: {
    'click-outside': {
      bind(el: any, binding: any) {
        el._clickOutside = (event: Event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el: any) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },
  data() {
    return {
      inputValue: '',
      suggestions: [] as SuggestionItem[],
      showSuggestions: false,
      debounceTimer: null as ReturnType<typeof setTimeout> | null
    }
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  methods: {
    handleInput() {
      const keyword = this.inputValue.trim()
      if (!keyword) {
        this.suggestions = []
        this.showSuggestions = false
        return
      }
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions(keyword)
      }, 300)
    },
    async fetchSuggestions(keyword: string) {
      try {
        const res = await zzfetch({
          url: 'https://app.zhuanzhuan.com/api/zzlogic/getPcLegalRulesSuggestion',
          params: { searchK: keyword },
          method: 'POST'
        })
        if (res.respCode === 0) {
          this.suggestions = res.respData?.secondItems || []
          this.showSuggestions = this.suggestions.length > 0
        }
      } catch (e) {
        this.suggestions = []
        this.showSuggestions = false
      }
    },
    handleSearch() {
      const keyword = this.inputValue.trim()
      this.showSuggestions = false
      this.$emit('search', keyword)
    },
    handleSuggestionClick(item: SuggestionItem) {
      this.showSuggestions = false
      if (item.jumpUrl) {
        window.open(item.jumpUrl, '_blank')
      }
    },
    handleClear() {
      this.inputValue = ''
      this.suggestions = []
      this.showSuggestions = false
      this.$emit('search', '')
    },
    closeSuggestions() {
      this.showSuggestions = false
    }
  }
})
</script>

<style scoped lang="scss">
.legal-search {
  position: relative;
  width: 100%;

  &__bar {
    display: flex;
    align-items: stretch;
    border: 1px solid #ff1027;
    border-radius: 8px;
    overflow: hidden;
    background: #ff1027;

    &:focus-within {
      border-color: #ff1027;
    }
  }

  &__input-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    position: relative;
    background: #fff;
  }

  &__input {
    width: 100%;
    height: 40px;
    padding: 0 36px 0 16px;
    border: none;
    outline: none;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 22px;
    color: #111;
    background: transparent;

    &::placeholder {
      color: #bbb;
    }
  }

  &__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #999;
    cursor: pointer;
    border-radius: 50%;
    background: #eee;

    &:hover {
      color: #666;
      background: #ddd;
    }
  }

  &__btn {
    width: 80px;
    height: 40px;
    border: none;
    background: #ff1027;
    color: #fff;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background: #ff1027;
    }
  }

  &__suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 300px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
    padding: 4px;
  }

  &__suggestion-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 0 12px;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      background: #f5f5f5;
    }
  }

  &__suggestion-text {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: normal;
    line-height: 22px;
    letter-spacing: normal;
    color: #111;
    flex: 1;
  }

  &__suggestion-arrow {
    font-size: 16px;
    color: #ccc;
    margin-left: 12px;
  }
}
</style>
